import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Rating,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCash, IconLock, IconUsers } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "../../../../gql/__generated__/graphql";
import CollabModal, {
  BrandCollabFormValues,
  CreatorCollabFormValues,
} from "../../../pages/DiscoverPage/components/CollabModal";
import SocialMediaStats from "../../../pages/ProfilePage/components/SocialMediaStats";
import { calculateTotalFollowers } from "../../../utils/dataUtils";
import { isLoggedIn } from "../../../utils/jwtUtils";

export interface SocialMedia {
  tiktok?: number;
  instagram?: number;
  youtube?: number;
  twitter?: number;
}

export interface CreatorSummary {
  id: string;
  name: string;
  image: string;
  rating: number;
  description: string;
  monthlyGMV?: number;
  socialMedia: SocialMedia;
  categories: string[];
  completedCollabs: number;
}

interface CreatorCardProps {
  creator: User;
  products?: any[]; // Products passed from parent
  setupComplete?: boolean; // Whether the user's profile setup is complete
}

function CreatorCard({
  creator,
  products = [],
  setupComplete = true,
}: CreatorCardProps) {
  const navigate = useNavigate();

  // React State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const userIsLoggedIn = isLoggedIn();

  // Memoize the total followers calculation
  const totalFollowers = useMemo(() => {
    return calculateTotalFollowers(creator);
  }, [creator.socialMedia, creator.tiktokData]);

  // Format follower counts (e.g., 1500 -> 1.5K)
  function formatFollowers(count: number): string {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }

    return count.toString();
  }

  // Format currency (e.g., 15000 -> $15,000)
  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  }

  const handleCardClick = () => {
    navigate(`/profile/${creator.email}`);
  };

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      h="100%"
      onClick={isModalOpen ? undefined : handleCardClick}
      style={{ cursor: isModalOpen ? "default" : "pointer", transition: "transform 0.2s, box-shadow 0.2s" }}
      onMouseEnter={(e) => {
        if (!isModalOpen) {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 8px 16px rgba(0, 0, 0, 0.1)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "";
      }}
    >
      <Stack h="100%" justify="space-between" gap="md">
          <Flex
            direction={{ base: "column", sm: "row" }}
            gap="md"
            align={{ base: "center", sm: "flex-start" }}
          >
            {/* Avatar */}
            <Avatar
              src={creator.imageUrl}
              size={80}
              radius={80}
              alt={`${creator.name}'s profile picture`}
            />

            {/* Creator info */}
            <Stack gap="xs" style={{ flex: 1 }}>
              <Group justify="space-between" wrap="nowrap">
                <Box>
                  <Text fw={700} size="xl">
                    {creator.name}
                  </Text>
                  <Group gap="xs">
                    <Rating value={creator.rating || 0} readOnly fractions={2} />
                    <Text size="sm" c="dimmed">
                      ({(creator.rating || 0).toFixed(1)})
                    </Text>
                  </Group>
                </Box>

                {/* Total follower badge - visible on mobile and desktop */}
                {totalFollowers > 0 && (
                  <Badge
                    size="lg"
                    variant="light"
                    leftSection={<IconUsers size={14} />}
                    styles={{
                      root: {
                        whiteSpace: "nowrap",
                        minWidth: "fit-content",
                        overflow: "visible",
                      },
                      label: {
                        overflow: "visible",
                        textOverflow: "clip",
                      },
                    }}
                  >
                    {formatFollowers(totalFollowers)}
                  </Badge>
                )}
              </Group>

              <Text size="sm" lineClamp={3}>
                {creator.description || "No description available"}
              </Text>
            </Stack>
          </Flex>

        <Box>
          {/* Categories and Monthly GMV */}
          <SimpleGrid cols={2} mt="md">
            <Group gap="xs">
              {creator.categories?.map((category) => (
                <Badge key={category} variant="outline" size="sm">
                  {category}
                </Badge>
              )) || null}
            </Group>

            {creator.tiktokData?.creatorStats?.med_gmv_revenue?.value && (
              <Badge
                size="lg"
                color="green"
                variant="light"
                leftSection={<IconCash size={14} />}
              >
                {formatCurrency(
                  parseFloat(creator.tiktokData.creatorStats.med_gmv_revenue.value)
                )}
                /mo GMV
              </Badge>
            )}
          </SimpleGrid>

          <Divider my="md" />

          {/* Social Media Section */}
          <Flex gap="md" wrap="wrap" justify="space-between" align="center">
            {(creator.socialMedia || creator.tiktokData) && (
              <SocialMediaStats
                socialMedia={creator.socialMedia ?? null}
                tiktokData={creator.tiktokData}
                variant="inline"
              />
            )}

            <Tooltip
              label={
                !userIsLoggedIn
                  ? "Please log in or sign up to collaborate with creators"
                  : !setupComplete
                  ? "Complete your profile setup to start collaborating"
                  : ""
              }
              disabled={userIsLoggedIn && setupComplete}
              position="top"
              withArrow
            >
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  if (userIsLoggedIn && setupComplete) {
                    setIsModalOpen(true);
                  } else if (!userIsLoggedIn) {
                    navigate("/login");
                  }
                }}
                disabled={!userIsLoggedIn || !setupComplete}
                leftSection={
                  !userIsLoggedIn || !setupComplete ? (
                    <IconLock size={16} />
                  ) : undefined
                }
              >
                {!userIsLoggedIn
                  ? "Log In to Collab"
                  : !setupComplete
                  ? "Complete Profile"
                  : "Collab"}
              </Button>
            </Tooltip>
          </Flex>
        </Box>
      </Stack>

      {/* <Group pt={"xs"} justify="flex-end">
        <Button
          onClick={() => {
            setIsModalOpen(true);
          }}
        >
          Collab
        </Button>
      </Group> */}

      <CollabModal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isBrand={true}
        creator={creator}
        products={products}
        onSubmit={function (
          _values: BrandCollabFormValues | CreatorCollabFormValues
        ): void {
          console.log("successful collab sent");
        }}
      />
    </Card>
  );
}

export default CreatorCard;
